<template>
  <CustomPopover :target="`popover-tgt-${record.id}`"
                 ref="popover"
                 triggers="hover"
                 placement="top"
                 :arrow="false"
                 @show="loadDetails"
                 custom-class="record-popover">
    <div v-if="record">
      <div v-if="record.record_type">
        <b>{{ $t('record_type') }}</b>: <LoadingDots v-if="!detailsLoaded"/><span v-else>{{ recordType }}</span>
      </div>
      <div v-if="patient">
        <b>{{ $t('patient') }}:</b> {{ patientName }}
        <span v-if="patient.phone && !rights.includes('hide_patient_phone')">({{ patient.phone }})</span>
        <span class="records-count ml-2 circle text-center circle-primary"
              v-if="patient.client_records && patient.client_records.length > 1">
        {{ patient.client_records.length }}
      </span>
        <div v-if="patient.date_of_birth">
          <span><b>{{ $t('age') }}:</b> {{ patient.age }} ({{ formatDate(patient.date_of_birth) }})</span>
        </div>
      </div>
      <div v-if="doctor"><b>{{$t('doctor')}}:</b> {{ doctor.full_name }}</div>
      <div v-if="record.author"><b>{{$t('added')}}:</b> {{ record.author.full_name }} </div>
      <div><b>{{ $t('status')}}:</b> {{ $t(record.iconStatus) }}<span v-if="openedClosedStatus" class="ml-1">({{ $t(openedClosedStatus) }})</span></div>
      <div v-if="patient && patient.card"><b>{{ $t('card_number')}}:</b> {{ patient.card }}</div>
      <div v-if="record.description">
      <hr class="my-2"/>
      <div class="text-muted mb-0">{{ $t('record_comment') }}</div>
      <div class="word-break">{{ record.description }}</div>
      </div>
      <div class="mt-3" v-if="showPlannedServices && record.planned_services && record.planned_services.length">
        <b>{{ $t('planned_services') }}: </b>
        <span v-for="(ps, psind) in record.planned_services"
              :key="`plan-sp-${psind}`">{{ psind > 0 ? ', ' : ''}}{{ ps.name }}</span>
      </div>
      <div v-if="record.tags && record.tags.length">
        <span><b>{{ $t('record_tags') }}: </b></span>
        <span>{{ record.tags.map(tag => tag.title).join(', ') }}</span>
      </div>
      <div v-if="patient && patient.tags && patient.tags.length">
        <span><b>{{ $t('tags') }}: </b></span>
        <span>{{ patient.tags.map(tag => tag.title).join(', ') }}</span>
      </div>
      <div v-if="pinnedComment">
        <hr class="my-2"/>
        <div class="text-muted mb-0">{{ $t('patient_comment') }}</div>
        <div style="white-space: pre-line" v-html="pinnedComment"/>
      </div>
      <PatientNotesBlock :notes="notes"/>
    </div>
  </CustomPopover>
</template>

<script setup>
import {computed, getCurrentInstance, onBeforeMount, ref} from "vue"
import { useStore } from "vuex"
import { formatDate } from "@/extensions/filters/filters"
import CustomPopover from "./CustomPopover.vue"
import {CalendarService} from "@/services/api/calendar.api.service"
import PatientNotesBlock from "@/components/parts/patients/PatientNotesBlock.vue"
import LoadingDots from "@/components/parts/general/LoadingDots.vue"

const { record } = defineProps({
  record: {
    type: Object,
    required: true
  },
  doctor: {
    type: Object
  },
})

const store = useStore()
const details = ref(null)
const detailsLoaded = ref(false)
let i18n = null

const patient = computed(() => record.patient || {})
const patientName = computed(() => patient.value?.full_name || '')
const rights = computed(() => store.state.auth.rights)
const showPlannedServices = computed(() => store.state.calendar.showPlannedServices)
const pinnedComment = computed(() => details.value?.pinnedComments?.[0]?.text || null)
const notes = computed(() => details.value?.notes || [])
const openedClosedStatus = computed(() => {
  if(record.isClosed) return 'closed'
  if(record.opened_visit) return 'opened'
  return null
})
const recordType = computed(() => {
  return details.value?.recordType?.title || i18n.t(record.record_type)
})

onBeforeMount(() => {
  i18n = (getCurrentInstance())?.ctx?.$i18n
})

const loadDetails = async () => {
  if(details.value) return
  try {
    const res = await CalendarService.loadRecordPopupDetails(record.id)
    details.value = res.data
  } catch (e) {
    console.log(e)
  } finally {
    detailsLoaded.value = true
  }
}
</script>

<style scoped lang="scss">
.records-count {
  font-size: 10px;
  font-weight: bold;
  color: #fff;
}
</style>
